import { useEffect, useMemo, useState } from 'react';
import { Gauge } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from '../components/PageLoader';
import { InlineError } from '../components/InlineError';
import type { UsageRecord } from '../lib/types';

const num = (n: number) => (n ?? 0).toLocaleString();

const ACTION_LABELS: Record<string, string> = {
    query: 'Query', review: 'Review', extract: 'Extraction',
    'extract:library': 'Extraction (whole library)', 'extract:focused': 'Extraction (focused)',
    agent: 'Chats', 'agent:assistant': 'Chats from AI assistants', draft: 'Document drafts', commentary: 'Report commentary', chat_title: 'Chat titles', other: 'Other',
};
const actionLabel = (a: string) => ACTION_LABELS[a] ?? a;

const formatDay = (d: string) =>
    new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
    });

export default function UsagePage() {
    const { authFetch } = useAuth();
    const [rows, setRows] = useState<UsageRecord[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        authFetch('/status/usage')
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load usage')))
            .then((data: UsageRecord[]) => setRows(data))
            .catch(err => { setError(err.message); setRows([]); });
    }, [authFetch]);

    const totals = useMemo(() => {
        const r = rows ?? [];
        return {
            tokens: r.reduce((s, x) => s + (x.total_tokens || 0), 0),
            calls: r.reduce((s, x) => s + (x.call_count || 0), 0),
        };
    }, [rows]);

    if (rows === null) return <PageLoader label="Loading usage..." />;

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-brand" /> Usage
                </h3>
                <p className="text-sm text-coolslate mt-1">
                    LLM token usage for your workspace, by day and action.
                </p>
            </div>

            {error && <InlineError message={error} />}

            {rows.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-6">
                    <div className="border border-softgrey rounded-xl px-5 py-3">
                        <div className="text-[12px] text-coolslate">Total tokens</div>
                        <div className="text-xl font-semibold text-graphite">{num(totals.tokens)}</div>
                    </div>
                    <div className="border border-softgrey rounded-xl px-5 py-3">
                        <div className="text-[12px] text-coolslate">Total calls</div>
                        <div className="text-xl font-semibold text-graphite">{num(totals.calls)}</div>
                    </div>
                </div>
            )}

            {rows.length === 0 ? (
                <p className="text-sm text-coolslate py-16 text-center">No usage recorded yet.</p>
            ) : (
                <div className="border border-softgrey rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-offwhite text-coolslate text-left">
                                    <th className="px-4 py-2.5 font-medium">Date</th>
                                    <th className="px-4 py-2.5 font-medium">Action</th>
                                    <th className="px-4 py-2.5 font-medium">Model</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Calls</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Input</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Cache read</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Cache write</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Output</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Total tokens</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => {
                                    // Only show the date when it changes, so days read as groups.
                                    const newDay = i === 0 || rows[i - 1].usage_date !== r.usage_date;
                                    return (
                                        <tr key={r.id} className={`border-t border-softgrey ${newDay ? '' : ''}`}>
                                            <td className="px-4 py-2.5 text-graphite whitespace-nowrap">
                                                {newDay ? formatDay(r.usage_date) : ''}
                                            </td>
                                            <td className="px-4 py-2.5 text-graphite">{actionLabel(r.action)}</td>
                                            <td className="px-4 py-2.5 text-coolslate">{r.model || '—'}</td>
                                            <td className="px-4 py-2.5 text-coolslate text-right">{num(r.call_count)}</td>
                                            <td className="px-4 py-2.5 text-coolslate text-right">{num(r.input_tokens)}</td>
                                            <td className="px-4 py-2.5 text-coolslate text-right">{num(r.cache_read_tokens)}</td>
                                            <td className="px-4 py-2.5 text-coolslate text-right">{num(r.cache_write_tokens)}</td>
                                            <td className="px-4 py-2.5 text-coolslate text-right">{num(r.output_tokens)}</td>
                                            <td className="px-4 py-2.5 text-graphite text-right font-medium">{num(r.total_tokens)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
